import React, { useCallback, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from "@tanstack/react-table";
import "./dashboard.scss";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ContentContainer from "templates/content-container/contentContainer";
type DayKey = "mon" | "tue" | "wed" | "thu" | "fri";

type RowData = {
  id: number;
  origin: string;
  destination: string;

  monDispatches: number;
  tueDispatches: number;
  wedDispatches: number;
  thuDispatches: number;
  friDispatches: number;
};

const initialData: RowData[] = [
  {
    id: 1,
    origin: "ANA",
    destination: "BWI",
    monDispatches: 5,
    tueDispatches: 3,
    wedDispatches: 6,
    thuDispatches: 2,
    friDispatches: 4,
  },
  {
    id: 2,
    origin: "CHI",
    destination: "ATL",
    monDispatches: 8,
    tueDispatches: 1,
    wedDispatches: 4,
    thuDispatches: 6,
    friDispatches: 3,
  },
  {
    id: 3,
    origin: "BWI",
    destination: "DAL",
    monDispatches: 2,
    tueDispatches: 5,
    wedDispatches: 3,
    thuDispatches: 7,
    friDispatches: 2,
  },
];

const dayConfig: {
  key: DayKey;
  label: string;
  dispatchesField: keyof RowData;
}[] = [
    {
      key: "mon",
      label: "Monday",
      dispatchesField: "monDispatches",
    },
    {
      key: "tue",
      label: "Tuesday",
      dispatchesField: "tueDispatches",
    },
    {
      key: "wed",
      label: "Wednesday",
      dispatchesField: "wedDispatches",
    },
    {
      key: "thu",
      label: "Thursday",
      dispatchesField: "thuDispatches",
    },
    {
      key: "fri",
      label: "Friday",
      dispatchesField: "friDispatches",
    },
  ];

type SortKey = "origin" | "destination" | "totalDispatches";

type SortState = {
  key: SortKey;
  direction: "asc" | "desc";
};

export default function StaticLoadPlanTable() {
  const columnHelper = createColumnHelper<RowData>();

  const [includedDays, setIncludedDays] = useState<Record<DayKey, boolean>>({
    mon: true,
    tue: true,
    wed: true,
    thu: true,
    fri: true,
  });

  const [sort, setSort] = useState<SortState>({
    key: "origin",
    direction: "asc",
  });

  const toggleDay = (day: DayKey) => {
    setIncludedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const getRowTotals = useCallback((row: RowData) => {
    return dayConfig.reduce(
      (acc, day) => {
        if (!includedDays[day.key]) {
          return acc;
        }
        acc.dispatches += Number(row[day.dispatchesField] ?? 0);
        return acc;
      },
      {
        dispatches: 0,
      }
    );
  }, [includedDays]);

  /**
   * Custom sort outside TanStack.
   * We sort the data before passing it into useReactTable.
   */

  const sortedData = useMemo(() => {
    const cloned = [...initialData];

    cloned.sort((a, b) => {
      if (sort.key === "totalDispatches") {
        const aValue = getRowTotals(a).dispatches;
        const bValue = getRowTotals(b).dispatches;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aValue = String(a[sort.key]);
      const bValue = String(b[sort.key]);
      const comparison = aValue.localeCompare(bValue);
      return sort.direction === "asc" ? comparison : -comparison;
    });

    return cloned;
  }, [sort, getRowTotals]);

  const handleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const sortLabel = useCallback((key: SortKey) => {
    if (sort.key !== key) return "";
    return sort.direction === "asc" ? " ▲" : " ▼";
  }, [sort.key, sort.direction]);

  const columns = useMemo<ColumnDef<RowData, any>[]>(
    () => [
      columnHelper.accessor("origin", {
        header: () => (
          <button className="sortButton" onClick={() => handleSort("origin")}>
            Origin{sortLabel("origin")}
          </button>
        ),
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("destination", {
        header: () => (
          <button
            className="sortButton"
            onClick={() => handleSort("destination")}
          >
            Destination{sortLabel("destination")}
          </button>
        ),
        cell: (info) => info.getValue(),
      }),

      columnHelper.group({
        id: "totals",
        header: "",
        columns: [
          columnHelper.display({
            id: "totalDispatches",
            header: () => (
              <button
                className="sortButton"
                onClick={() => handleSort("totalDispatches")}
              >
                Total Dispatches{sortLabel("totalDispatches")}
              </button>
            ),
            cell: ({ row }) => getRowTotals(row.original).dispatches,
          }),
        ],
      }),

      ...dayConfig.map((day) =>
        columnHelper.group({
          id: day.key,
          header: () => (
            <label className="groupHeaderCheckbox">
              <input
                type="checkbox"
                checked={includedDays[day.key]}
                onChange={() => toggleDay(day.key)}
              />
              <span>{day.label}</span>
            </label>
          ),
          columns: [
            columnHelper.accessor(day.dispatchesField, {
              id: `${day.key}Dispatches`,
              header: "Dispatches",
              cell: (info) => (
                <span
                  className={
                    includedDays[day.key] ? "" : "excludedCell"
                  }
                >
                  {info.getValue()}
                </span>
              ),
            }),
          ],
        })
      ),
    ],
    [includedDays, getRowTotals, columnHelper, sortLabel]
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    getCoreRowModel: getCoreRowModel(),

    /**
     * Important:
     * Do not add getSortedRowModel here because sorting is custom.
     */
  });

  const grandTotals = useMemo(() => {
    return sortedData.reduce(
      (acc, row) => {
        const totals = getRowTotals(row);
        acc.dispatches += totals.dispatches;
        return acc;
      },
      {
        dispatches: 0,
      }
    );
  }, [sortedData, getRowTotals]);

  return (
    <ContentContainer
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar title="Static Load Dashboard" mods={{ "full-width": true }} />
      }
    >
      <div>
        <div className="tableScrollWrapper">
          <table className="loadPlanTable">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}


                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {

                    return (
                      <td
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              <tr className="grandTotalRow">
                <td className="stickyLeft">Total</td>
                <td></td>
                <td className="grandTotalCell">
                  {grandTotals.dispatches}
                </td>
                {dayConfig.map((day) => (
                  <React.Fragment key={day.key}>
                    <td className={includedDays[day.key] ? "" : "excludedCell"}>
                      {includedDays[day.key]
                        ? sortedData.reduce(
                          (sum, row) =>
                            sum + Number(row[day.dispatchesField] ?? 0),
                          0
                        )
                        : "-"}
                    </td>

                  </React.Fragment>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ContentContainer>

  );
}